import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Plus, Edit2, Trash2, Shield, Mail, Phone,
  Check, X, MoreVertical, UserPlus, Filter, Download, Loader2, AlertCircle,
  Eye, Package, Clock, ShoppingCart, Calendar, DollarSign
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'customer',
    password: '',
  });

  // Fetch all users and admin users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Fetch ALL registered users from the new endpoint
      const allUsersRes = await fetch(`${API_URL}/api/admin/all-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (allUsersRes.ok) {
        const allUsersData = await allUsersRes.json();
        setUsers(allUsersData);
        // Separate admin users for the admin section
        setAdminUsers(allUsersData.filter(u => u.role === 'admin'));
      } else {
        // Fallback to old endpoint if new one fails
        const adminRes = await fetch(`${API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (adminRes.ok) {
          const admins = await adminRes.json();
          setAdminUsers(admins);
          setUsers(admins);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // State for audit trail modal
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedUserAudit, setSelectedUserAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Fetch audit trail for a user
  const fetchAuditTrail = async (userId) => {
    setLoadingAudit(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/audit-trail`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedUserAudit(data);
        setShowAuditModal(true);
      } else {
        toast.error('Failed to load audit trail');
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoadingAudit(false);
    }
  };

  // Update user status
  const updateUserStatus = async (userId, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/user/${userId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`User status updated to ${newStatus}`);
        fetchUsers(); // Refresh list
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Use users directly since we now get all from API
  const allUsers = users;

  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (user.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-white/90 text-[#0a1628]',
      staff: 'bg-white/70 text-[#0a1628]',
      driver: 'bg-white/60 text-[#0a1628]',
      customer: 'bg-white/40 text-[#0a1628]',
    };
    return <Badge className={styles[role] || styles.customer}>{role.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? <Badge className="bg-white text-[#0a1628]">Active</Badge>
      : <Badge className="bg-white/30 text-white">Inactive</Badge>;
  };

  // Create new admin user via API
  const handleCreateAdmin = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || null
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create admin');
      }
      
      const newAdmin = await res.json();
      setAdminUsers([...adminUsers, newAdmin]);
      setShowAddAdminModal(false);
      setFormData({ name: '', email: '', phone: '', role: 'customer', password: '' });
      toast.success('Admin user created successfully!');
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.message || 'Failed to create admin user');
    } finally {
      setSaving(false);
    }
  };

  // Delete admin user
  const handleDeleteAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete admin');
      }
      
      setAdminUsers(adminUsers.filter(a => a.id !== userId));
      toast.success('Admin user deleted successfully');
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.message || 'Failed to delete admin user');
    }
  };

  const handleAddUser = () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const newUser = {
      id: (users.length + 10).toString(),
      ...formData,
      status: 'active',
      created_at: new Date().toISOString().split('T')[0],
    };
    
    setUsers([...users, newUser]);
    setShowAddModal(false);
    setFormData({ name: '', email: '', phone: '', role: 'customer', password: '' });
    toast.success('User created successfully');
  };

  const handleEditUser = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const token = localStorage.getItem('auth_token');
    
    try {
      // If role changed, call the role update API
      if (formData.role !== editingUser.role) {
        const roleResponse = await fetch(`${API_URL}/api/superadmin/users/${editingUser.id}/role`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            role: formData.role,
            reason: `Role changed from ${editingUser.role} to ${formData.role} via User Management`
          })
        });
        
        if (!roleResponse.ok) {
          const error = await roleResponse.json();
          throw new Error(error.detail || 'Failed to update role');
        }
      }
      
      // Update other user details if needed
      const updateResponse = await fetch(`${API_URL}/api/superadmin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        })
      });
      
      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        throw new Error(error.detail || 'Failed to update user');
      }
      
      // Update local state
      setUsers(users.map(u => 
        u.id === editingUser.id 
          ? { ...u, name: formData.name, email: formData.email, phone: formData.phone, role: formData.role }
          : u
      ));
      
      // Update admin users list if role changed to/from admin
      if (formData.role === 'admin' && editingUser.role !== 'admin') {
        setAdminUsers([...adminUsers, { ...editingUser, ...formData }]);
      } else if (formData.role !== 'admin' && editingUser.role === 'admin') {
        setAdminUsers(adminUsers.filter(u => u.id !== editingUser.id));
      }
      
      setEditingUser(null);
      setFormData({ name: '', email: '', phone: '', role: 'customer', password: '' });
      toast.success('User updated successfully');
      
      // Refresh the data
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = (userId, isAdmin = false) => {
    if (isAdmin) {
      handleDeleteAdmin(userId);
    } else {
      if (window.confirm('Are you sure you want to delete this user?')) {
        setUsers(users.filter(u => u.id !== userId));
        toast.success('User deleted successfully');
      }
    }
  };

  const handleToggleStatus = (userId) => {
    setUsers(users.map(u => 
      u.id === userId 
        ? { ...u, status: u.status === 'active' ? 'inactive' : 'active' }
        : u
    ));
    toast.success('User status updated');
  };

  const openEditModal = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      password: '',
    });
    setEditingUser(user);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowAddAdminModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', phone: '', role: 'customer', password: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0a1628] animate-spin mx-auto mb-4" />
          <p className="text-[#0a1628]/70">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0a1628] flex items-center gap-3">
              <Users className="w-8 h-8 text-[#0a1628]" />
              User Management
            </h1>
            <p className="text-[#0a1628]/60 mt-1">Create, edit and manage user accounts</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="flex items-center gap-2 border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button 
              onClick={() => setShowAddAdminModal(true)}
              className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white flex items-center gap-2"
              data-testid="add-admin-btn"
            >
              <Shield className="w-4 h-4" />
              Add Admin
            </Button>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-[#0a1628]/20 hover:bg-[#0a1628]/30 text-[#0a1628] flex items-center gap-2 border border-[#0a1628]/30"
            >
              <UserPlus className="w-4 h-4" />
              Add User
            </Button>
          </div>
        </div>

        {/* Admin Users Section */}
        <Card className="bg-white border-[#0a1628]/20 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-[#0a1628]" />
            <h2 className="text-xl font-bold text-[#0a1628]">Administrator Accounts</h2>
            <Badge className="bg-[#0a1628] text-white">{adminUsers.length} admins</Badge>
          </div>
          
          {adminUsers.length === 0 ? (
            <div className="text-center py-8 text-[#0a1628]/60">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No admin users found</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {adminUsers.map((admin) => (
                <div key={admin.id} className="bg-[#0a1628]/5 rounded-xl p-4 border border-[#0a1628]/20">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#0a1628] flex items-center justify-center text-white font-bold text-lg">
                        {admin.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-[#0a1628]">{admin.name}</p>
                        <p className="text-sm text-[#0a1628]/60">{admin.email}</p>
                        {admin.phone && (
                          <p className="text-xs text-[#0a1628]/40">{admin.phone}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAdmin(admin.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-admin-${admin.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge className="bg-[#0a1628] text-white">ADMIN</Badge>
                    <Badge className="bg-[#0a1628]/20 text-[#0a1628]">Active</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-[#0a1628]/20 p-4">
            <p className="text-sm text-[#0a1628]/60">Total Users</p>
            <p className="text-2xl font-bold text-[#0a1628]">{allUsers.length}</p>
          </Card>
          <Card className="bg-white border-[#0a1628]/20 p-4">
            <p className="text-sm text-[#0a1628]/60">Active Users</p>
            <p className="text-2xl font-bold text-[#0a1628]">{allUsers.filter(u => u.status === 'active').length}</p>
          </Card>
          <Card className="bg-white border-[#0a1628]/20 p-4">
            <p className="text-sm text-[#0a1628]/60">Customers</p>
            <p className="text-2xl font-bold text-[#0a1628]">{allUsers.filter(u => u.role === 'customer').length}</p>
          </Card>
          <Card className="bg-white border-[#0a1628]/20 p-4">
            <p className="text-sm text-[#0a1628]/60">Drivers</p>
            <p className="text-2xl font-bold text-[#0a1628]">{allUsers.filter(u => u.role === 'driver').length}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-[#0a1628]/20 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0a1628]/40" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px] bg-white border-[#0a1628]/20 text-[#0a1628]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#0a1628]/20">
                <SelectItem value="all" className="text-[#0a1628]">All Roles</SelectItem>
                <SelectItem value="admin" className="text-[#0a1628]">Admin</SelectItem>
                <SelectItem value="staff" className="text-[#0a1628]">Staff</SelectItem>
                <SelectItem value="driver" className="text-[#0a1628]">Driver</SelectItem>
                <SelectItem value="customer" className="text-[#0a1628]">Customer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white border-[#0a1628]/20 text-[#0a1628]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#0a1628]/20">
                <SelectItem value="all" className="text-[#0a1628]">All Status</SelectItem>
                <SelectItem value="active" className="text-[#0a1628]">Active</SelectItem>
                <SelectItem value="inactive" className="text-[#0a1628]">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="bg-white border-[#0a1628]/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a1628]/5 border-b border-[#0a1628]/20">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">Orders</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0a1628]/80">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#0a1628]/80">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0a1628]/10">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[#0a1628]/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          user.role === 'admin' ? 'bg-[#0a1628]' : 'bg-[#0a1628]/70'
                        }`}>
                          {(user.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[#0a1628]">{user.name || 'Unknown'}</p>
                          <p className="text-sm text-[#0a1628]/50">ID: {user.id?.slice(0, 8) || 'N/A'}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[#0a1628]">{user.email}</p>
                      <p className="text-sm text-[#0a1628]/50">{user.phone || 'No phone'}</p>
                    </td>
                    <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                    <td className="px-6 py-4">
                      <div className="text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0a1628]/10 text-[#0a1628] font-semibold text-sm">
                          {user.order_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#0a1628]/60 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchAuditTrail(user.id)}
                          className="border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10"
                          title="View Audit Trail"
                          data-testid={`audit-trail-${user.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {user.role !== 'admin' && user.role !== 'superadmin' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(user)}
                              className="border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10"
                              title="Edit User"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white border-[#0a1628]/20">
                                <DropdownMenuItem 
                                  onClick={() => updateUserStatus(user.id, user.status === 'active' ? 'inactive' : 'active')}
                                  className="text-[#0a1628] hover:bg-[#0a1628]/10"
                                >
                                  {user.status === 'active' ? 'Deactivate' : 'Activate'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateUserStatus(user.id, 'suspended')}
                                  className="text-amber-600 hover:bg-amber-50"
                                >
                                  Suspend
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteUser(user.id, user.role === 'admin')}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                        {user.role === 'admin' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(user)}
                              className="border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10"
                              title="Edit Admin"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAdmin(user.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-2 text-[#0a1628]/30" />
              <p className="text-[#0a1628]/60">No users found matching your criteria</p>
            </div>
          )}
        </Card>

        {/* Add Admin Modal */}
        {showAddAdminModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
            <Card className="relative z-10 w-full max-w-md bg-white border-[#0a1628]/20 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-[#0a1628]" />
                <h2 className="text-xl font-bold text-[#0a1628]">Create New Admin</h2>
              </div>
              
              <div className="bg-[#0a1628]/5 border border-[#0a1628]/20 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-[#0a1628]/70 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-[#0a1628]/80 font-medium">Admin Privileges</p>
                    <p className="text-xs text-[#0a1628]/60 mt-1">
                      Admin users have full access to all system features including user management, orders, products, and settings.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-[#0a1628]/80">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter full name"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                    data-testid="admin-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email address"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                    data-testid="admin-email-input"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Phone (Optional)</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 (613) 555-0123"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                    data-testid="admin-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Enter secure password"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                    data-testid="admin-password-input"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={closeModal} 
                    className="flex-1 border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateAdmin}
                    disabled={saving}
                    className="flex-1 bg-[#0a1628] hover:bg-[#0a1628]/90 text-white"
                    data-testid="create-admin-submit"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Create Admin
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Add/Edit User Modal */}
        {(showAddModal || editingUser) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
            <Card className="relative z-10 w-full max-w-md bg-white border-[#0a1628]/20 p-6">
              <h2 className="text-xl font-bold text-[#0a1628] mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#0a1628]/80">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter full name"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 (613) 555-0123"
                    className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                  />
                </div>
                <div>
                  <Label className="text-[#0a1628]/80">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({...formData, role: value})}
                  >
                    <SelectTrigger className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#0a1628]/20">
                      <SelectItem value="admin" className="text-[#0a1628]">Admin</SelectItem>
                      <SelectItem value="customer" className="text-[#0a1628]">Customer</SelectItem>
                      <SelectItem value="driver" className="text-[#0a1628]">Driver</SelectItem>
                      <SelectItem value="staff" className="text-[#0a1628]">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!editingUser && (
                  <div>
                    <Label className="text-[#0a1628]/80">Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Enter password"
                      className="mt-1 bg-white border-[#0a1628]/20 text-[#0a1628] placeholder:text-[#0a1628]/40"
                    />
                  </div>
                )}
                <div className="flex gap-4 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={closeModal} 
                    className="flex-1 border-[#0a1628]/30 text-[#0a1628] hover:bg-[#0a1628]/10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={editingUser ? handleEditUser : handleAddUser}
                    className="flex-1 bg-[#0a1628] hover:bg-[#0a1628]/90 text-white"
                  >
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Audit Trail Modal */}
        {showAuditModal && selectedUserAudit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-[#0a1628]/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-[#0a1628] flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    User Audit Trail
                  </h2>
                  <p className="text-[#0a1628]/60 text-sm mt-1">Complete activity history for this user</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    setShowAuditModal(false);
                    setSelectedUserAudit(null);
                  }}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* User Info Card */}
              <div className="bg-[#0a1628]/5 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#0a1628] flex items-center justify-center text-white text-xl font-bold">
                    {(selectedUserAudit.user?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#0a1628]">{selectedUserAudit.user?.name}</h3>
                    <p className="text-[#0a1628]/60 text-sm">{selectedUserAudit.user?.email}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge className="bg-[#0a1628]/10 text-[#0a1628] text-xs">
                        {selectedUserAudit.user?.role?.toUpperCase()}
                      </Badge>
                      <span className="text-xs text-[#0a1628]/50">
                        Joined: {selectedUserAudit.user?.created_at ? new Date(selectedUserAudit.user.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <Package className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{selectedUserAudit.activity_summary?.total_orders || 0}</p>
                  <p className="text-xs text-blue-600">Total Orders</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">${(selectedUserAudit.activity_summary?.total_spent || 0).toFixed(2)}</p>
                  <p className="text-xs text-green-600">Total Spent</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <ShoppingCart className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-700">{selectedUserAudit.activity_summary?.total_sessions || 0}</p>
                  <p className="text-xs text-purple-600">Sessions</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-700">{selectedUserAudit.activity_summary?.total_transfers || 0}</p>
                  <p className="text-xs text-amber-600">Transfers</p>
                </div>
              </div>

              {/* Orders List */}
              <div className="mb-6">
                <h4 className="font-semibold text-[#0a1628] mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Order History ({selectedUserAudit.orders?.length || 0})
                </h4>
                {selectedUserAudit.orders?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedUserAudit.orders.map((order, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[#0a1628]/5 rounded-lg">
                        <div>
                          <p className="font-medium text-[#0a1628]">#{order.order_number}</p>
                          <p className="text-xs text-[#0a1628]/50">
                            {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#0a1628]">${(order.total || 0).toFixed(2)}</p>
                          <Badge className={`text-xs ${
                            order.status === 'completed' ? 'bg-green-100 text-green-700' :
                            order.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#0a1628]/50 text-sm text-center py-4">No orders yet</p>
                )}
              </div>

              {/* Driver-specific extra data */}
              {selectedUserAudit.user?.extra_data && (
                <div className="mb-6">
                  <h4 className="font-semibold text-[#0a1628] mb-3">Role-Specific Data</h4>
                  <div className="bg-[#0a1628]/5 rounded-lg p-4">
                    {Object.entries(selectedUserAudit.user.extra_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1">
                        <span className="text-[#0a1628]/60 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-[#0a1628]">{value || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <Button 
                onClick={() => {
                  setShowAuditModal(false);
                  setSelectedUserAudit(null);
                }}
                className="w-full bg-[#0a1628] hover:bg-[#0a1628]/90 text-white"
              >
                Close
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
